import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiShieldKeyholeLine, RiDownloadLine, RiFileCopyLine } from '@remixicon/react';
import {
  Button,
  CopyButton,
  CopyableValue,
  ExternalLink,
  Flex,
  InfraCodeBlock,
  InfraField,
  InfraSection,
  InfraStack,
  Text,
  infraCardStyles as styles,
} from '../ui/InfraPrimitives';

export const SambaADInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const templateType = annotations['infrastructure.backstage.io/type'] || '';
  const environmentName = annotations['infrastructure.backstage.io/environment-name'];
  const publicIp = annotations['samba-ad.infrastructure/public-ip'];
  const instanceId = annotations['samba-ad.infrastructure/instance-id'];
  const region = annotations['samba-ad.infrastructure/region'];

  const domainName = annotations['samba-ad.infrastructure/domain-name'];
  const realm = annotations['samba-ad.infrastructure/realm'];
  const netbiosName = annotations['samba-ad.infrastructure/netbios-name'];
  const baseDn = annotations['samba-ad.infrastructure/base-dn'];
  const ldapUri = annotations['samba-ad.infrastructure/ldap-uri'];
  const ldapsUri = annotations['samba-ad.infrastructure/ldaps-uri'];
  const adminUser = annotations['samba-ad.infrastructure/admin-user'];
  const adminPassword = annotations['samba-ad.infrastructure/admin-password'];

  const usersCreated = annotations['samba-ad.infrastructure/users-created'];
  const groupsCreated = annotations['samba-ad.infrastructure/groups-created'];

  const sshPrivateKeyEncoded = annotations['samba-ad.infrastructure/ssh-private-key'];
  const sshCommand = annotations['samba-ad.infrastructure/ssh-command'];

  const hasSambaADInfo = templateType === 'aws-samba-ad-server' && (domainName || publicIp);

  if (!hasSambaADInfo) {
    return null;
  }

  const sshPrivateKey = sshPrivateKeyEncoded ? atob(sshPrivateKeyEncoded) : '';

  const downloadSSHKey = () => {
    if (!sshPrivateKeyEncoded) return;

    try {
      const decodedKey = atob(sshPrivateKeyEncoded);
      const blob = new Blob([decodedKey], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `samba-ad-${environmentName}.pem`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download SSH key:', error);
    }
  };

  const ldapConfigSnippet = `LDAP Server URL: ${ldapUri || 'ldap://<server-ip>:389'}
Base DN: ${baseDn || 'DC=example,DC=com'}
Bind DN: CN=${adminUser || 'Administrator'},CN=Users,${baseDn || 'DC=example,DC=com'}
Bind Password: ${adminPassword ? '***' : '<password>'}

User Search Filter: (sAMAccountName=%u)
Group Search Filter: (member=%D)`;

  return (
    <InfoCard
      title="🔐 Samba Active Directory Information"
      icon={<RiShieldKeyholeLine size={20} />}
    >
      <InfraStack>
        <InfraSection title="🌐 Domain Configuration">
          {domainName && <CopyableValue label="Domain:" value={domainName} />}
          {realm && <CopyableValue label="Realm:" value={realm} />}
          {netbiosName && <InfraField label="NetBIOS Name:" value={netbiosName} />}
          {baseDn && <CopyableValue label="Base DN:" value={baseDn} />}
        </InfraSection>

        <InfraSection title="🔌 LDAP Endpoints">
          {ldapUri && <CopyableValue label="LDAP URI:" value={ldapUri} />}
          {ldapsUri && <CopyableValue label="LDAPS URI:" value={ldapsUri} />}
          {publicIp && <CopyableValue label="Public IP:" value={publicIp} />}
        </InfraSection>

        {adminUser && adminPassword && (
          <InfraSection title="🔑 Administrator Credentials">
            <CopyableValue label="Username:" value={adminUser} />
            <InfraField
              label="Password:"
              value={
                <span className={styles.copyRow}>
                  <span>{'•'.repeat(adminPassword.length)}</span>
                  <CopyButton text={adminPassword} />
                </span>
              }
              mono={false}
            />
          </InfraSection>
        )}

        {(usersCreated || groupsCreated) && (
          <InfraSection title="👥 Users and Groups">
            {usersCreated && <InfraField label="Users Created:" value={usersCreated} />}
            {groupsCreated && <InfraField label="Groups Created:" value={groupsCreated} />}
          </InfraSection>
        )}

        {sshCommand && (
          <InfraSection title="🔐 SSH Access">
            <InfraField label="SSH Command:" value="" mono={false} />
            <div style={{ position: 'relative' }}>
              <InfraCodeBlock variant="light">{sshCommand}</InfraCodeBlock>
              <div style={{ position: 'absolute', top: 4, right: 4 }}>
                <CopyButton text={sshCommand} />
              </div>
            </div>
            {sshPrivateKeyEncoded && (
              <>
                <Text variant="title-small" style={{ marginTop: '1rem' }}>
                  SSH Private Key:
                </Text>
                <Flex gap="2">
                  <Button variant="primary" size="small" onPress={downloadSSHKey}>
                    <RiDownloadLine size={16} />
                    Download Key
                  </Button>
                  <Button
                    variant="secondary"
                    size="small"
                    onPress={() => navigator.clipboard.writeText(sshPrivateKey)}
                  >
                    <RiFileCopyLine size={16} />
                    Copy Key
                  </Button>
                </Flex>
              </>
            )}
          </InfraSection>
        )}

        <InfraSection title="📚 Redis Enterprise LDAP Integration">
          <Text variant="body-medium">
            Use the following configuration in Redis Enterprise:
          </Text>
          <InfraCodeBlock>{ldapConfigSnippet}</InfraCodeBlock>
          <ExternalLink href="https://redis.io/docs/latest/operate/rs/security/access-control/ldap/">
            View Redis LDAP Documentation →
          </ExternalLink>
        </InfraSection>

        {instanceId && region && (
          <InfraSection title="☁️ AWS Resources">
            <ExternalLink
              href={`https://console.aws.amazon.com/ec2/v2/home?region=${region}#Instances:instanceId=${instanceId}`}
            >
              View EC2 Instance in AWS Console →
            </ExternalLink>
          </InfraSection>
        )}
      </InfraStack>
    </InfoCard>
  );
};
